// Pure logic behind the install panel's build picker. Kept separate from the
// component so the rules that matter — never offer an unbuildable combination,
// never pre-select — are testable without rendering anything.

export type PublishedBuild = {
  id: string;
  displayName: string;
  version: string;
  apkUrl: string;
  hash: string;
  versionCode: number;
  builtAt: number;
};

export type BuildRow = {
  id: string;
  displayName: string;
  dev?: PublishedBuild;
  prod?: PublishedBuild;
  /**
   * Whether this target actually appears in the LOCAL repo's declared
   * build-targets.json (the `?env=local` list) — not just whether SOME row
   * exists for it. A row can exist purely because dev/prod published it,
   * while the working tree checked out here has no such target at all; `local`
   * builds from source, so without this the Local button would enable for a
   * target the installer then rejects with "unknown target". Left unset by
   * `mergeBuilds` (which only knows dev/prod) — the caller fills it in from
   * the local list.
   */
  local?: boolean;
};

export type InstallSource = 'local' | 'dev' | 'prod';

/**
 * WHICH SOURCES THIS CONSOLE MAY INSTALL FROM — DECIDED BY ITS OWN URL.
 *
 * 🔴 The source used to be a free choice defaulting to a hardcoded 'dev', which
 * meant an operator on admin.rubytill.com provisioning a real merchant's till
 * was pre-selected onto the dev-signed APK. Local, dev and prod are signed with
 * three different keys: the wrong one points the device at the wrong auth host,
 * fails to install over the other, and can need a factory reset to recover.
 *
 * The rule is therefore environment isolation, not a better default:
 *
 *   - a PROD console offers prod builds and nothing else
 *   - a DEV console offers dev builds and nothing else
 *   - a LOCAL console offers `local` and `dev`, and NEVER `prod`
 *
 * 🔴 LOCAL CANNOT INSTALL PROD, deliberately. It could until the local install
 * agent existed, because back then a laptop was the only thing that could reach
 * a till and so it had to be able to do everything. Now admin.rubytill.com can
 * install through the agent, which is where a production enrolment belongs: the
 * unit record, its timeline and its timestamps are written by the production
 * console, against production, with no question afterwards about which
 * environment a device was enrolled from. A prod-signed APK pushed from a
 * developer's tree leaves exactly that question, so the door is closed.
 *
 * Staging follows dev: the APK pipeline publishes two registries only, and
 * offering a prod-signed build to a non-prod fleet is the failure above.
 */
export function allowedSources(stage: 'prod' | 'staging' | 'dev', isLocalConsole: boolean): InstallSource[] {
  if (isLocalConsole) return ['local', 'dev'];
  return stage === 'prod' ? ['prod'] : ['dev'];
}

/**
 * The source a console starts on: the environment it is pointed at, narrowed to
 * what it is actually allowed to install.
 *
 * A hosted console has exactly one option and opens on it. A local console
 * opens on `dev` — it can never install prod, so there is nothing else it could
 * sensibly start on, even when pointed at production data for viewing.
 */
export function defaultSource(stage: 'prod' | 'staging' | 'dev', isLocalConsole: boolean): InstallSource {
  const allowed = allowedSources(stage, isLocalConsole);
  const want: InstallSource = stage === 'prod' ? 'prod' : 'dev';
  return allowed.includes(want) ? want : (allowed.includes('dev') ? 'dev' : allowed[0]);
}

/** One row per build target, carrying whichever environments published it. */
export function mergeBuilds(dev: PublishedBuild[], prod: PublishedBuild[]): BuildRow[] {
  const rows = new Map<string, BuildRow>();
  const put = (b: PublishedBuild, env: 'dev' | 'prod') => {
    const row = rows.get(b.id) || { id: b.id, displayName: b.displayName };
    row.displayName = row.displayName || b.displayName;
    row[env] = b;
    rows.set(b.id, row);
  };
  dev.forEach((b) => put(b, 'dev'));
  prod.forEach((b) => put(b, 'prod'));
  return [...rows.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Can this source install the picked build? `local` compiles from source, so it
 * needs no published artifact — but the target must still actually be declared
 * in THIS checkout's build-targets.json (`row.local`), or the installer rejects
 * it with "unknown target" after the button already looked available.
 */
export function isEnvAvailable(row: BuildRow | null, source: 'local' | 'dev' | 'prod'): boolean {
  if (!row) return false;
  if (source === 'local') return !!row.local;
  return !!row[source];
}

/**
 * Everything the Install button requires. Missing anything means disabled.
 *
 * 🔴 `timezone` IS REQUIRED, and is the one entry here that is not about
 * whether the run can technically succeed. An install with no zone chosen runs
 * perfectly and hands back a till whose clock is on whatever the factory set —
 * which is how an A10 reached a live New York counter on `Asia/Shanghai` and
 * stamped half a trading day onto the wrong date. See `timezones.ts`. It is
 * gated here, beside the build checks, so the panel refuses with a disabled
 * button rather than discovering it at a bench visit weeks later.
 */
export function canInstall(args: {
  unitId: string;
  targetId: string;
  source: 'local' | 'dev' | 'prod';
  rows: BuildRow[];
  timezone: string;
}): boolean {
  if (!args.unitId || !args.targetId || !args.timezone) return false;
  const row = args.rows.find((r) => r.id === args.targetId) || null;
  return isEnvAvailable(row, args.source);
}
